<template>
  <v-dialog :value="show" persistent max-width="520">
    <div class="apd-card pa-6">

      <!-- Header -->
      <div class="d-flex align-center mb-4" style="gap:10px">
        <div
          class="d-flex align-center justify-center"
          style="width:40px;height:40px;border-radius:10px;flex-shrink:0"
          :style="{
            background: status === 'done'  ? '#E1F5EE'
                      : status === 'error' ? '#FCEBEB'
                      : '#E6F1FB'
          }"
        >
          <v-icon v-if="status === 'running'" color="#185FA5">mdi-magnify-scan</v-icon>
          <v-icon v-else-if="status === 'done'"  color="#0F6E56">mdi-check-circle</v-icon>
          <v-icon v-else color="#E24B4A">mdi-alert-circle</v-icon>
        </div>
        <div>
          <div class="apd-overline mb-1">AML Pro · Suspicious Analysis</div>
          <div class="apd-title">
            {{ status === 'done'  ? 'Analysis complete'
             : status === 'error' ? 'An error occurred'
             : 'Running suspicious transaction analysis…' }}
          </div>
        </div>
      </div>

      <!-- Indeterminate bar while running -->
      <div v-if="status === 'running'" class="apd-indeterminate-track mb-2">
        <div class="apd-indeterminate-bar" />
      </div>

      <!-- Determinate bar once done -->
      <template v-else>
        <div class="apd-progress-track mb-2">
          <div
            class="apd-progress-fill"
            :style="{
              width: '100%',
              background: status === 'error' ? '#E24B4A' : '#1D9E75'
            }"
          />
        </div>
      </template>

      <div class="d-flex justify-space-between mb-4" style="font-size:11px;color:#999">
        <span v-if="status === 'running'">Evaluating rules against imported transactions…</span>
        <span v-else-if="status === 'done'">
          {{ rulesEvaluated.toLocaleString() }} rule{{ rulesEvaluated !== 1 ? 's' : '' }} evaluated ·
          {{ transactionsAnalysed.toLocaleString() }} transaction{{ transactionsAnalysed !== 1 ? 's' : '' }} analysed
        </span>
        <span v-else>{{ message }}</span>
        <span v-if="status === 'running'" class="apd-pulse">●</span>
      </div>

      <!-- Result stat chips (shown after completion) -->
      <v-row v-if="status === 'done'" dense class="mb-4">
        <v-col cols="4">
          <div class="apd-stat-chip" style="background:#E6F1FB">
            <div class="apd-stat-value" style="color:#0C447C">{{ rulesEvaluated.toLocaleString() }}</div>
            <div class="apd-stat-label" style="color:#185FA5">Rules evaluated</div>
          </div>
        </v-col>
        <v-col cols="4">
          <div class="apd-stat-chip" style="background:#E1F5EE">
            <div class="apd-stat-value" style="color:#0F6E56">{{ transactionsAnalysed.toLocaleString() }}</div>
            <div class="apd-stat-label" style="color:#1D9E75">Transactions analysed</div>
          </div>
        </v-col>
        <v-col cols="4">
          <div class="apd-stat-chip" :style="alertsCreated > 0 ? 'background:#FCEBEB' : 'background:#F1EFE8'">
            <div class="apd-stat-value" :style="alertsCreated > 0 ? 'color:#A32D2D' : 'color:#888'">{{ alertsCreated.toLocaleString() }}</div>
            <div class="apd-stat-label" :style="alertsCreated > 0 ? 'color:#E24B4A' : 'color:#aaa'">Alerts created</div>
          </div>
        </v-col>
      </v-row>

      <!-- Status message -->
      <div
        class="apd-status-msg mb-4"
        :style="status === 'error' ? 'color:#791F1F;background:#FCEBEB' : ''"
      >
        <v-icon x-small left :color="status === 'error' ? '#791F1F' : status === 'done' ? '#0F6E56' : '#185FA5'">
          {{ status === 'error' ? 'mdi-alert' : status === 'done' ? 'mdi-check' : 'mdi-information-outline' }}
        </v-icon>
        {{ message || 'Preparing analysis…' }}
      </div>

      <!-- Footer -->
      <div class="d-flex justify-end">
        <button
          class="apd-btn"
          :disabled="status === 'running'"
          :style="status === 'running' ? 'opacity:.4;cursor:not-allowed' : ''"
          @click="$emit('close')"
        >
          {{ status === 'done' || status === 'error' ? 'Close' : 'Processing…' }}
        </button>
      </div>

    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'AnalysisProgressDialog',
  props: {
    show:                 { type: Boolean, required: true },
    status:               { type: String,  default: 'running' }, // 'running' | 'done' | 'error'
    message:              { type: String,  default: '' },
    rulesEvaluated:       { type: Number,  default: 0 },
    transactionsAnalysed: { type: Number,  default: 0 },
    alertsCreated:        { type: Number,  default: 0 },
  },
};
</script>

<style scoped>
.apd-card { border: 0.5px solid #e0e0e0; border-radius: 12px; background: white; }
.apd-overline { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; color: #999; }
.apd-title { font-size: 16px; font-weight: 500; color: #222; }

.apd-progress-track { height: 8px; border-radius: 8px; background: #eee; overflow: hidden; }
.apd-progress-fill  { height: 100%; border-radius: 8px; transition: width .6s ease, background .3s; }

/* Indeterminate animated bar */
.apd-indeterminate-track {
  height: 8px; border-radius: 8px; background: #eee; overflow: hidden; position: relative;
}
.apd-indeterminate-bar {
  position: absolute; top: 0; left: -40%;
  width: 40%; height: 100%; border-radius: 8px;
  background: linear-gradient(90deg, transparent, #185FA5, transparent);
  animation: apd-slide 1.4s ease-in-out infinite;
}
@keyframes apd-slide {
  0%   { left: -40%; }
  100% { left: 140%; }
}

.apd-pulse {
  color: #185FA5;
  animation: apd-blink 1.2s step-start infinite;
}
@keyframes apd-blink {
  50% { opacity: 0; }
}

.apd-stat-chip { border-radius: 8px; padding: 10px 8px; text-align: center; }
.apd-stat-value { font-size: 20px; font-weight: 600; line-height: 1; margin-bottom: 4px; }
.apd-stat-label { font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: .05em; }

.apd-status-msg {
  font-size: 12px; color: #555; background: #f7f9fc;
  border-radius: 8px; padding: 10px 12px;
  display: flex; align-items: center; gap: 6px;
}

.apd-btn {
  font-size: 13px; background: #185FA5; color: #fff;
  border: none; border-radius: 8px; padding: 7px 20px;
  display: inline-flex; align-items: center; gap: 4px; cursor: pointer;
  font-family: inherit;
}
</style>